'use client';
// 电话输入框原始复合组件，但也是基于FormProvider的方式来处理表单的变化

import { Country } from '@/api/types/public';
import ActionSheet from '@/components/ActionSheet';
import FormInput, { InputProps } from '@/components/Form/FormInput';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import PopperMenu, { type MenuOption } from '@/components/SortMenuWithPopper/PopperMenu';
import { env } from '@/utils/env';
import { formatPhoneNoByRule } from '@/utils/form-rule';
import { parsePhoneValue } from '@/utils/tools';
import { InputAdornment } from '@mui/material';
import { DownOutlined } from '@zeelool/icon';
import { omit } from 'lodash-es';
import React, { useEffect, useId, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import Autocomplete from '../AutoComplete';

export type OriginalPhoneInputProps = Omit<InputProps, 'formatInput' | 'rules'> & {
  /**
   * 默认值, 可为以下格式:
   * 1. "US(+1)" - 仅国家代码
   * 2. "US(+1)1234567890" - 国家代码+电话号码
   */
  defaultValue?: string;

  value?: string;
  /**
   * 电话号码变化回调
   */
  onChange?: (telephone?: string) => void;
  /**
   * 国家列表
   */
  countryList: Country[];
  /**
   * 国家代码，支持外部输入
   */
  telCode?: string;
  /** M 端国家区号选择器的展示方式，默认沿用 ActionSheet。 */
  mobileCountrySelectorVariant?: 'action-sheet' | 'compact-dropdown';
  /** Raises nested country selectors above a parent modal when needed. */
  overlayZIndex?: number;
};

/** 短信弹窗紧凑区号浮层的容器样式。 */
const COMPACT_COUNTRY_MENU_STYLES: React.CSSProperties = {
  boxSizing: 'border-box',
  width: '100px',
  minWidth: '100px',
  height: '186px',
  padding: '12px 0 6px 12px',
  overflow: 'hidden',
  borderRadius: '4px',
  backgroundColor: '#ffffff',
  boxShadow: '0 2px 2px rgba(0, 0, 0, 0.1)',
};

/** 短信弹窗紧凑区号浮层的选项样式。 */
const COMPACT_COUNTRY_ITEM_STYLES: React.CSSProperties & {
  '&:hover, &.Mui-focusVisible': React.CSSProperties;
  '&::before': React.CSSProperties;
} = {
  boxSizing: 'border-box',
  position: 'relative',
  width: '88px',
  // 覆盖项目主题在 600px 以下为 MenuItem 注入的 46px 最小高度。
  minHeight: '18px !important',
  height: '18px',
  padding: 0,
  fontFamily: 'var(--font-bricolage)',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '18px',
  color: '#282828',
  backgroundColor: '#ffffff',
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: '#f5f6f7',
  },
  // 不改变 Figma 的 18px 视觉行高，将上下命中范围各扩展 3px。
  '&::before': {
    position: 'absolute',
    inset: '-3px 0',
    content: '""',
  },
};

/** 短信弹窗紧凑区号浮层的已选项样式。 */
const COMPACT_COUNTRY_SELECTED_ITEM_STYLES: React.CSSProperties & {
  '&:hover': React.CSSProperties;
} = {
  color: '#282828',
  backgroundColor: '#ffffff',
  '&:hover': {
    backgroundColor: '#f5f6f7',
  },
};

/** 组合国家区号选择与电话号码输入的原始表单组件。 */
const OriginalPhoneInput: React.FC<OriginalPhoneInputProps> = ({
  id = '',
  defaultValue = env.NEXT_PUBLIC_LOCALE_DEFAULT_TELCODE,
  value,
  onChange,
  layout = 'inline',
  slotProps = {},
  name,
  control,
  countryList,
  telCode,
  mobileCountrySelectorVariant = 'action-sheet',
  overlayZIndex,
  ...restProps
}) => {
  // 解析默认值
  const { telCode: initialTelCode, telephone: initialTelephone } = parsePhoneValue(defaultValue);

  const form = useForm({
    defaultValues: {
      telephone: initialTelephone === '' ? undefined : initialTelephone,
      telCode: initialTelCode,
    },
  });

  const [showTelCodePopup, setShowTelCodePopup] = useState(false);
  const countryCodeTriggerRef = useRef<HTMLButtonElement>(null);
  const countryCodeTriggerId = useId();
  const countryCodeListId = useId();
  const isCompactCountrySelector = mobileCountrySelectorVariant === 'compact-dropdown';
  const isCountrySelectorDisabled = Boolean(restProps.disabled || countryList.length === 0);
  const usesInsideFloatingLabel = restProps.floatingLabelVariant === 'inside';

  // 当外部defaultValue变化时更新内部表单值
  useEffect(() => {
    if (defaultValue) {
      const { telCode: telCodeValue, telephone } = parsePhoneValue(defaultValue);
      form.setValue('telCode', telCodeValue);
      form.setValue('telephone', telephone);
    }
  }, [defaultValue, form]);

  // 当外部value变化时更新内部表单值
  useEffect(() => {
    if (value !== undefined && value !== null) {
      const { telCode: telCodeValue, telephone } = parsePhoneValue(value);
      form.setValue('telCode', telCodeValue);
      form.setValue('telephone', telephone);
    }
  }, [value, form]);

  const columns = countryList.map((item) => ({
    label: `${item.country_id}(+${item.tel_code})`,
    value: item,
  }));

  /** 将国家列表转换成紧凑浮层可复用的选项结构。 */
  const compactCountryOptions: MenuOption[] = countryList.map((item) => ({
    label: `${item.country_id}(+${item.tel_code})`,
    value: item.id,
  }));

  useEffect(() => {
    if (telCode) {
      form.setValue('telCode', telCode);
    }
  }, [telCode]);

  // 使用useWatch更高效地监听特定字段
  const innerTelCode = useWatch({
    control: form.control,
    name: 'telCode',
  });
  const selectedCompactCountryId = countryList.find(
    (item) => `${item.country_id}(+${item.tel_code})` === innerTelCode,
  )?.id;

  /** 关闭紧凑区号浮层，并保留当前交互目标的焦点。 */
  const closeCompactCountrySelector = () => {
    setShowTelCodePopup(false);
  };

  /** 关闭紧凑区号浮层，并将键盘焦点交还给触发按钮。 */
  const closeCompactCountrySelectorAndRestoreFocus = () => {
    closeCompactCountrySelector();
    countryCodeTriggerRef.current?.focus();
  };

  /** 选择区号后立即写回表单并关闭紧凑浮层。 */
  const handleCompactCountrySelect = (_value: string | number, option: MenuOption) => {
    form.setValue('telCode', option.label);
    closeCompactCountrySelectorAndRestoreFocus();
  };

  /** 在紧凑浮层内按 Escape 时仅关闭区号选择器。 */
  const handleCompactCountryKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || !showTelCodePopup) return;

    event.preventDefault();
    event.stopPropagation();
    closeCompactCountrySelectorAndRestoreFocus();
  };

  const telephone = useWatch({
    control: form.control,
    name: 'telephone',
  });

  // 电话号码格式化函数
  const formatPhoneInput = (_value: string): string => {
    const formTelCode = form.getValues('telCode');
    if (!_value) {
      return _value;
    }
    if (formTelCode === 'US(+1)') {
      return formatPhoneNoByRule(_value, '(###)###-####');
    } else {
      return _value.replace(/\D/g, '');
    }
  };

  useEffect(() => {
    const number = formatPhoneInput(telephone);
    form.setValue('telephone', number);
  }, [innerTelCode]);

  useEffect(() => {
    // 为了解决一开始默认就会触发校验的问题，通过telephone的值来判断是否需要触发校验
    if (innerTelCode && telephone !== undefined) {
      onChange(`${innerTelCode}${telephone}`);
    }
  }, [innerTelCode, telephone]);

  return (
    <>
      <WapOnly>
        {!isCompactCountrySelector && (
          <ActionSheet
            open={showTelCodePopup}
            zIndex={overlayZIndex}
            onClose={() => {
              setShowTelCodePopup(false);
            }}
            title=""
            onSave={(country) => {
              form.setValue('telCode', `${country.country_id}(+${country.tel_code})`);

              setShowTelCodePopup(false);
            }}
            defaultValue={initialTelCode}
            options={columns}
          />
        )}
      </WapOnly>
      <FormProvider {...form}>
        <FormInput
          {...omit(restProps, ['value'])}
          id={id}
          name="telephone"
          control={form.control}
          layout={layout}
          formatInput={formatPhoneInput}
          useNativeError
          sx={{
            '& .MuiInputBase-root': {
              paddingRight: 0,
              paddingLeft: 0,
              '& .MuiInputAdornment-root': {
                margin: '0',
                height: '100%',
                maxHeight: 'none',
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px',
              height: '100%',
              boxSizing: 'border-box',
            },
            ...restProps.sx,
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{
                    '& .MuiInputLabel-root.Mui-error': {
                      color: 'red',
                    },
                  }}
                >
                  <WapOnly>
                    {isCompactCountrySelector ? (
                      <>
                        <button
                          ref={countryCodeTriggerRef}
                          id={countryCodeTriggerId}
                          aria-controls={showTelCodePopup ? countryCodeListId : undefined}
                          aria-expanded={showTelCodePopup}
                          aria-haspopup="menu"
                          className="flex h-[46px] w-[100px] items-center gap-[10px] border-0 bg-transparent p-0 pl-[12px] font-br text-[13px] leading-[18px] text-primary-black disabled:cursor-not-allowed"
                          data-testid="phone-country-code-trigger"
                          disabled={isCountrySelectorDisabled}
                          type="button"
                          onClick={() => {
                            setShowTelCodePopup((isOpen) => !isOpen);
                          }}
                          onKeyDown={handleCompactCountryKeyDown}
                        >
                          <span>{innerTelCode}</span>
                          <DownOutlined className="text-[12px] text-secondary-text" />
                        </button>
                        <PopperMenu
                          anchorEl={countryCodeTriggerRef.current}
                          open={showTelCodePopup}
                          options={compactCountryOptions}
                          selectedValue={selectedCompactCountryId}
                          minWidth={100}
                          placement="bottom-start"
                          customMenuStyles={COMPACT_COUNTRY_MENU_STYLES}
                          menuItemStyles={COMPACT_COUNTRY_ITEM_STYLES}
                          selectedItemStyles={COMPACT_COUNTRY_SELECTED_ITEM_STYLES}
                          menuListProps={{
                            id: countryCodeListId,
                            'aria-labelledby': countryCodeTriggerId,
                            onKeyDown: handleCompactCountryKeyDown,
                            sx: {
                              display: 'flex',
                              height: '168px',
                              flexDirection: 'column',
                              gap: '12px',
                              overflowY: 'auto',
                              p: 0,
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#eeeeee transparent',
                              '&::-webkit-scrollbar': {
                                width: '1px',
                              },
                              '&::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                minHeight: '30px',
                                borderRadius: '90px',
                                backgroundColor: '#eeeeee',
                              },
                            },
                          }}
                          popperProps={{
                            open: showTelCodePopup,
                            modifiers: [
                              {
                                name: 'offset',
                                options: { offset: [0, -3] },
                              },
                              {
                                name: 'flip',
                                enabled: false,
                              },
                              {
                                name: 'preventOverflow',
                                options: { padding: 4 },
                              },
                            ],
                            style: { zIndex: overlayZIndex ?? 1400 },
                          }}
                          onClose={closeCompactCountrySelector}
                          onSelect={handleCompactCountrySelect}
                        />
                      </>
                    ) : (
                      <div
                        className={`flex items-center ${usesInsideFloatingLabel ? 'h-[28px] translate-y-[8px] gap-[12px] pl-[12px]' : 'h-[48px]'}`}
                        onClick={() => {
                          setShowTelCodePopup(true);
                        }}
                      >
                        <span className="text-primary-black font-br text-14 shrink-0 whitespace-nowrap">
                          {innerTelCode}
                        </span>
                        <DownOutlined className="text-12 text-secondary-text" />
                      </div>
                    )}
                  </WapOnly>
                  <WebOnly>
                    <Autocomplete
                      disableClearable
                      control={form.control}
                      name="telCode"
                      floatingLabelVariant={usesInsideFloatingLabel ? 'inside' : undefined}
                      options={countryList.map((item) => `${item.country_id}(+${item.tel_code})`)}
                      popperZIndex={overlayZIndex}
                      formItemClassName=""
                      sx={{
                        '& .MuiAutocomplete-inputRoot': {
                          marginTop: usesInsideFloatingLabel ? '0px' : '2px',
                          ...(usesInsideFloatingLabel
                            ? {
                                height: '50px',
                                minHeight: '50px',
                                paddingTop: '0px !important',
                                paddingBottom: '0px !important',
                                alignItems: 'flex-start',
                                flexWrap: 'nowrap',
                                width: 'max-content',
                                gap: '12px',
                              }
                            : {}),
                        },
                        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                          textAlign: 'left',
                          ...(usesInsideFloatingLabel
                            ? {
                                minWidth: '0',
                                width: `${Math.max(innerTelCode?.length ?? 0, 7)}ch`,
                                flexGrow: 0,
                                padding: '21px 0 6px 12px !important',
                                height: '100%',
                                boxSizing: 'border-box',
                                lineHeight: '19px',
                              }
                            : {
                                minWidth: '108px',
                                paddingLeft: '16px',
                              }),
                        },
                        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-endAdornment':
                          usesInsideFloatingLabel
                            ? {
                                position: 'static',
                                top: 'auto',
                                right: 'auto',
                                transform: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                width: '16px',
                                height: '16px',
                                marginTop: '28px',
                                flexShrink: 0,
                              }
                            : {
                                right: '0px',
                              },
                        ...(usesInsideFloatingLabel
                          ? {
                              '& .MuiAutocomplete-inputRoot .MuiAutocomplete-popupIndicator': {
                                width: '16px',
                                minWidth: '16px',
                                height: '16px',
                                padding: 0,
                                margin: 0,
                              },
                            }
                          : {}),
                        '& .MuiFormControl-root': {
                          marginTop: '0px',
                        },
                        '& .MuiOutlinedInput-root': {
                          paddingLeft: '0px',
                          paddingRight: usesInsideFloatingLabel
                            ? '0px !important'
                            : '8px !important',
                        },
                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                          borderLeft: 0,
                          borderTop: 0,
                          borderBottom: 0,
                          borderRight: 0,
                          // paddingRight: 0,
                          paddingLeft: 0,
                          // borderRightColor: '#d4d4d4 !important',
                        },
                      }}
                    />
                  </WebOnly>
                </InputAdornment>
              ),
              ...slotProps.input,
            },
            htmlInput: {
              maxLength: innerTelCode === 'US(+1)' ? 13 : 15,
              style: { width: '100%' },
              ...slotProps.htmlInput,
            },
            ...slotProps,
          }}
        />
      </FormProvider>
    </>
  );
};

export default OriginalPhoneInput;
